import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UserInfo from "./AllAvailabilities";
import { saveOtherMedicalAvailability } from "../../redux/slices/availabilitySlice.js/otherMedicalAvailabilitySlice";

const formatDate = (d) => d.toISOString().split("T")[0];

// ================= WEEK GENERATOR =================
const getWeek = () => {
  const days = [];

  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);

    days.push({
      day: d.toLocaleDateString("en-US", { weekday: "long" }),
      date: formatDate(d),
      isHoliday: false,
      sessions: [],
    });
  }

  return days;
};

// ================= TIME HELPERS =================
const toMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

const nowMinutes = () => {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
};

const isValidTime = (start, end) => {
  const max = 22 * 60;

  const s = toMinutes(start);
  const e = toMinutes(end);

  return s !== null && e !== null && s < e && s >= nowMinutes() && e <= max;
};

// ================= COMPONENT =================
const SessionAvailabilityManager = () => {
  const dispatch = useDispatch();

  const token = useSelector((s) => s.auth.token);
  const user = useSelector((s) => s.auth.user);

  const [weekly, setWeekly] = useState(getWeek());

  // ================= ADD SESSION =================
  const addSession = (dayIndex) => {
    const copy = [...weekly];

    if (copy[dayIndex].sessions.length >= 6) {
      alert("Max 6 sessions allowed per day");
      return;
    }

    copy[dayIndex].sessions.push({
      startTime: "",
      endTime: "",
    });

    setWeekly(copy);
  };

  // ================= UPDATE SESSION =================
  const updateSession = (dIndex, sIndex, field, value) => {
    const copy = [...weekly];
    copy[dIndex].sessions[sIndex][field] = value;
    setWeekly(copy);
  };

  // ================= REMOVE SESSION =================
  const removeSession = (dIndex, sIndex) => {
    const copy = [...weekly];
    copy[dIndex].sessions.splice(sIndex, 1);
    setWeekly(copy);
  };

  // ================= COPY TO ALL =================
  const copyToAll = (index) => {
    const copy = [...weekly];
    const source = copy[index];

    setWeekly(
      copy.map((d) => ({
        ...d,
        isHoliday: source.isHoliday,
        sessions: structuredClone(source.sessions),
      })),
    );
  };

  // ================= HOLIDAY =================
  const toggleHoliday = (i) => {
    const copy = [...weekly];
    copy[i].isHoliday = !copy[i].isHoliday;

    if (copy[i].isHoliday) {
      copy[i].sessions = [];
    }

    setWeekly(copy);
  };

  // ================= SAVE =================
  const handleSave = async () => {
    if (!token || !user?._id) return;

    // VALIDATION
    for (const d of weekly) {
      if (!d.isHoliday) {
        if (d.sessions.length === 0) {
          alert(`Add at least 1 session in ${d.day}`);
          return;
        }

        for (const s of d.sessions) {
          if (!isValidTime(s.startTime, s.endTime)) {
            alert(
              `Invalid session in ${d.day} (${s.startTime} - ${s.endTime})`,
            );
            return;
          }
        }

        // OVERLAP CHECK
        const sorted = [...d.sessions].sort(
          (a, b) => toMinutes(a.startTime) - toMinutes(b.startTime),
        );

        for (let i = 0; i < sorted.length - 1; i++) {
          if (
            toMinutes(sorted[i].endTime) > toMinutes(sorted[i + 1].startTime)
          ) {
            alert(`Overlapping sessions in ${d.day}`);
            return;
          }
        }
      }
    }

    const payload = {
      weeklyAvailability: weekly.map((d) => ({
        day: d.day,
        date: d.date,
        isHoliday: d.isHoliday,
        sessions: d.sessions,
      })),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    await dispatch(saveOtherMedicalAvailability({ data: payload, token }));
    alert("Saved successfully");
  };

  // ================= UI =================
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">
        🏥 Session-Based Availability Manager
      </h2>

      <UserInfo />

      {weekly.map((d, i) => (
        <div key={d.date} className="border p-4 mb-3 rounded">
          {/* HEADER */}
          <div className="flex justify-between items-center">
            <h3 className="font-bold">
              {d.day} ({d.date})
            </h3>

            <div className="flex gap-2">
              <button
                onClick={() => toggleHoliday(i)}
                className={`px-2 py-1 rounded text-white ${
                  d.isHoliday ? "bg-red-600" : "bg-green-600"
                }`}
              >
                {d.isHoliday ? "Holiday" : "Working"}
              </button>

              <button
                onClick={() => copyToAll(i)}
                className="bg-blue-600 text-white px-2 py-1 rounded"
              >
                Copy All
              </button>

              <button
                onClick={() => addSession(i)}
                className="bg-purple-600 text-white px-2 py-1 rounded"
              >
                + Add Session
              </button>
            </div>
          </div>

          {/* SESSIONS */}
          {!d.isHoliday &&
            d.sessions.map((s, si) => (
              <div
                key={si}
                className="flex gap-2 items-center mt-3 bg-gray-50 p-2 rounded"
              >
                <input
                  type="time"
                  value={s.startTime}
                  onChange={(e) =>
                    updateSession(i, si, "startTime", e.target.value)
                  }
                  className="border p-2 rounded"
                  max="22:00"
                />

                <span>to</span>

                <input
                  type="time"
                  value={s.endTime}
                  onChange={(e) =>
                    updateSession(i, si, "endTime", e.target.value)
                  }
                  className="border p-2 rounded"
                  max="22:00"
                />

                <button
                  onClick={() => removeSession(i, si)}
                  className="bg-red-600 text-white px-2 rounded"
                >
                  ✕
                </button>
              </div>
            ))}
        </div>
      ))}

      {/* SAVE */}
      <button
        onClick={handleSave}
        className="w-full mt-5 bg-green-600 text-white py-3 rounded"
      >
        💾 Save Availability
      </button>
    </div>
  );
};

export default SessionAvailabilityManager;
